/**
 * Desconto à vista do plano de assinatura (`pr_discount_on_payment_in_cash`), com as MESMAS regras
 * do pay (`fallbackCycleBase` / `ApplySubscriptionDiscountOnPaymentInCashService`):
 *
 * - só vale quando a contratação é em 1x (Pix é sempre 1x);
 * - só nos ciclos bimestral, trimestral, semestral e anual;
 * - desconto < R$ 1 ou maior que o preço é ignorado.
 *
 * A ordem também é a do pay: primeiro o desconto à vista, depois o cupom sobre o que sobrou.
 *
 * Por que existe (16/10/2026): o plano "Evolution APP Anual" da pro_universo (R$ 11.964,00 com
 * R$ 1.000,00 à vista) mostrava "Total R$ 11.964,00" no resumo e "1x de R$ 10.964,00" no seletor
 * de parcelas, sem dizer de onde vinha a diferença. E a tela dava o desconto em qualquer ciclo,
 * inclusive no mensal, onde o pay não aplica.
 */
import { IPlan } from '../api/plans/interfaces/planInterface';
import { ICoupon } from '../api/coupons/interfaces/couponInterface';
import { couponDiscountFor } from './couponDisplay';

/** Bimestral, trimestral, semestral e anual (`PlanCycle` do pay). */
const CYCLES_WITH_CASH_DISCOUNT = [4, 5, 6, 7];

const round2 = (value: number) => Math.round(value * 100) / 100;

/** O desconto à vista que o pay aplica neste plano em 1x (0 quando não aplica). */
export const planCashDiscount = (
	plan?: Pick<IPlan, 'price' | 'cycle' | 'discountOnPaymentInCash'> | null
): number => {
	if (!plan) return 0;
	const price = Number(plan.price) || 0;
	const discount = Number(plan.discountOnPaymentInCash) || 0;
	if (!CYCLES_WITH_CASH_DISCOUNT.includes(Number(plan.cycle))) return 0;
	if (discount < 1 || discount > price) return 0;
	return round2(discount);
};

type CouponLike = Pick<
	ICoupon,
	'discountType' | 'discountValue' | 'flatDiscount'
> | null;

/**
 * Valor do ciclo que o comprador paga: preço − desconto à vista (se 1x) − cupom (sobre o que sobrou).
 * Sem juros — os juros do parcelamento entram por cima, no seletor de parcelas.
 */
export const planChargeBreakdown = (
	plan: Pick<IPlan, 'price' | 'cycle' | 'discountOnPaymentInCash'>,
	coupon: CouponLike | undefined,
	paidInCash: boolean
) => {
	const price = Number(plan.price) || 0;
	const cashDiscount = paidInCash ? planCashDiscount(plan) : 0;
	const base = round2(price - cashDiscount);
	const couponDiscount = couponDiscountFor(base, coupon);
	return {
		price,
		cashDiscount,
		/** Base do cupom e do ciclo (preço já sem o desconto à vista). */
		base,
		couponDiscount,
		total: round2(base - couponDiscount)
	};
};
